import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { isAdminHost, urlOnPublic, urlOnAdmin, shouldCrossHost } from "../lib/hosts";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    // Admin lives on switch subdomain, partner lives on the main site.
    // If the user signed in on the "wrong" host, hard-nav them to the right one.
    const role = res.user.role;
    if (role === "admin") {
      if (shouldCrossHost() && !isAdminHost()) {
        window.location.replace(urlOnAdmin("/admin"));
      } else {
        nav("/admin");
      }
    } else if (role === "partner") {
      if (shouldCrossHost() && isAdminHost()) {
        window.location.replace(urlOnPublic("/partner"));
      } else {
        nav("/partner");
      }
    } else {
      nav("/");
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-6">
      <div className="w-full max-w-md">
        {isAdminHost() ? (
          <a href={urlOnPublic("/")} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-black mb-6" data-testid="back-to-public">
            <ArrowLeft className="w-4 h-4" /> Back to offer.express
          </a>
        ) : (
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-black mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        )}
        <div className="bg-white border border-black/8 rounded-xl p-8">
          <div className="badge-mono text-neutral-500 mb-3">Sign in</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter mb-1">Welcome back</h1>
          <p className="text-sm text-neutral-500 mb-6">
            {isAdminHost() ? "Admin console" : "Partner console"}
          </p>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-neutral-500">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-neutral-500">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={submitting} className="w-full h-11 bg-black hover:bg-[#0055FF] text-white">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
